import {
  useEffect,
  useState
} from 'react';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


function apiHeaders() {
  return {
    apikey: SUPABASE_KEY,
    Authorization:
      'Bearer ' +
      SUPABASE_KEY
  };
}


async function readJson(
  response,
  label
) {
  const text =
    await response.text();

  if (!response.ok) {
    throw new Error(
      label +
        ' failed (' +
        response.status +
        '): ' +
        (
          text ||
          response.statusText
        )
    );
  }

  return text
    ? JSON.parse(text)
    : [];
}


async function fetchReadings(
  courseId
) {
  if (!courseId) {
    return [];
  }

  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/course_week_reading_assignments' +
        '?select=' +
        'id,' +
        'display_order,' +
        'course_weeks!inner(id,course_id,week_number,class_date),' +
        'course_readings(id,title,author,drive_file_id,drive_file_name)' +
        '&course_weeks.course_id=eq.' +
        encodeURIComponent(
          courseId
        ),
      {
        headers:
          apiHeaders()
      }
    );

  const rows =
    await readJson(
      response,
      'Loading course readings'
    );

  return (
    Array.isArray(rows)
      ? rows
      : []
  ).sort(
    (a, b) => {
      const aWeek =
        Number(
          a?.course_weeks
            ?.week_number
        ) || 0;

      const bWeek =
        Number(
          b?.course_weeks
            ?.week_number
        ) || 0;

      if (aWeek !== bWeek) {
        return aWeek - bWeek;
      }

      const aOrder =
        Number(
          a?.display_order
        ) || 0;

      const bOrder =
        Number(
          b?.display_order
        ) || 0;

      if (aOrder !== bOrder) {
        return (
          aOrder - bOrder
        );
      }

      return String(
        a?.course_readings
          ?.title ||
        ''
      ).localeCompare(
        String(
          b?.course_readings
            ?.title ||
          ''
        )
      );
    }
  );
}


export default function StudentReadings({
  course
}) {
  const [
    readings,
    setReadings
  ] = useState([]);

  const [
    previewReading,
    setPreviewReading
  ] = useState(null);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    error,
    setError
  ] = useState('');


  useEffect(() => {
    let cancelled =
      false;

    async function load() {
      setLoading(true);
      setError('');

      try {
        const rows =
          await fetchReadings(
            course?.id
          );

        if (!cancelled) {
          setReadings(rows);
        }

      } catch (err) {
        console.error(
          'Loading course readings failed:',
          err
        );

        if (!cancelled) {
          setError(
            err?.message ||
              'Could not load readings.'
          );
        }

      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }


    if (course?.id) {
      load();
    } else {
      setReadings([]);
      setLoading(false);
    }


    return () => {
      cancelled = true;
    };

  }, [
    course?.id
  ]);


  return (
    <div
      style={
        styles.wrap
      }
    >
      <h1
        style={
          styles.heading
        }
      >
        Readings
      </h1>

      {error && (
        <div
          style={
            styles.error
          }
        >
          {error}
        </div>
      )}

      {loading ? (
        <div
          style={
            styles.muted
          }
        >
          Loading readings...
        </div>

      ) : readings.length === 0 ? (
        <div
          style={
            styles.empty
          }
        >
          No readings are currently assigned to this course.
        </div>

      ) : (
        <div
          style={
            styles.list
          }
        >
          {readings.map(
            row => {
              const reading =
                row.course_readings ||
                {};

              const weekNumber =
                row.course_weeks
                  ?.week_number;

              const hasFile =
                !!reading
                  .drive_file_id;

              return (
                <div
                  key={row.id}
                  style={
                    styles.row
                  }
                >
                  <div
                    style={
                      styles.readingMain
                    }
                  >
                    <span
                      style={
                        styles.week
                      }
                    >
                      Week {
                        weekNumber ??
                        '—'
                      }
                    </span>

                    <button
                      type="button"
                      style={
                        styles.titleButton
                      }
                      disabled={
                        !hasFile
                      }
                      onClick={() =>
                        hasFile &&
                        setPreviewReading(
                          reading
                        )
                      }
                    >
                      {reading.title ||
                        'Untitled Reading'}

                      {reading.author
                        ? ' by ' +
                          reading.author
                        : ''}
                    </button>
                  </div>

                  <div
                    style={
                      styles.actions
                    }
                  >
                    {hasFile ? (
                      <>
                        <button
                          type="button"
                          style={
                            styles.openButton
                          }
                          onClick={() =>
                            setPreviewReading(
                              reading
                            )
                          }
                        >
                          Open
                        </button>

                        <a
                          href={
                            'https://drive.google.com/uc?export=download&id=' +
                            reading
                              .drive_file_id
                          }
                          target="_blank"
                          rel="noreferrer"
                          style={
                            styles.downloadButton
                          }
                        >
                          Download
                        </a>
                      </>
                    ) : (
                      <span
                        style={
                          styles.noFile
                        }
                      >
                        No file
                      </span>
                    )}
                  </div>
                </div>
              );
            }
          )}
        </div>
      )}


      {previewReading && (
        <div
          style={
            styles.overlay
          }
          onClick={() =>
            setPreviewReading(
              null
            )
          }
        >
          <div
            style={
              styles.preview
            }
            onClick={
              event =>
                event.stopPropagation()
            }
          >
            <div
              style={
                styles.previewHeader
              }
            >
              <div
                style={
                  styles.previewHeading
                }
              >
                <strong>
                  {
                    previewReading
                      .title ||
                    'Untitled Reading'
                  }
                </strong>

                {previewReading
                  .author && (
                  <span>
                    by {
                      previewReading
                        .author
                    }
                  </span>
                )}
              </div>

              <button
                type="button"
                style={
                  styles.closeButton
                }
                onClick={() =>
                  setPreviewReading(
                    null
                  )
                }
              >
                ×
              </button>
            </div>

            <iframe
              src={
                'https://drive.google.com/file/d/' +
                previewReading
                  .drive_file_id +
                '/preview'
              }
              title={
                previewReading
                  .title ||
                'Course reading'
              }
              style={
                styles.previewFrame
              }
            />

            <div
              style={
                styles.previewFooter
              }
            >
              <a
                href={
                  'https://drive.google.com/uc?export=download&id=' +
                  previewReading
                    .drive_file_id
                }
                target="_blank"
                rel="noreferrer"
                style={
                  styles.downloadButton
                }
              >
                Download
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const styles = {
  wrap: {
    minWidth: 0
  },

  heading: {
    margin:
      '0 0 18px',
    color:
      '#243b53',
    fontSize:
      '26px',
    fontWeight:
      '700'
  },

  list: {
    display:
      'flex',
    flexDirection:
      'column',
    gap:
      '7px'
  },

  row: {
    display:
      'flex',
    alignItems:
      'center',
    flexWrap:
      'wrap',
    gap:
      '12px',
    padding:
      '12px 14px',
    background:
      'white',
    border:
      '1px solid #d9e2ec',
    borderRadius:
      '6px'
  },

  readingMain: {
    display:
      'flex',
    alignItems:
      'baseline',
    flexWrap:
      'wrap',
    gap:
      '8px',
    flex:
      '1 1 320px',
    minWidth: 0
  },

  week: {
    color:
      '#2f6f9f',
    fontSize:
      '13px',
    fontWeight:
      '800',
    whiteSpace:
      'nowrap'
  },

  titleButton: {
    appearance:
      'none',
    border:
      'none',
    background:
      'transparent',
    padding: 0,
    margin: 0,
    color:
      '#243b53',
    fontFamily:
      'inherit',
    fontSize:
      '14px',
    lineHeight:
      '1.45',
    textAlign:
      'left',
    cursor:
      'pointer'
  },

  actions: {
    display:
      'flex',
    alignItems:
      'center',
    gap:
      '7px',
    marginLeft:
      'auto'
  },

  openButton: {
    border:
      '1px solid #bcccdc',
    background:
      'white',
    color:
      '#334e68',
    borderRadius:
      '5px',
    padding:
      '6px 10px',
    fontSize:
      '12px',
    fontWeight:
      '700',
    cursor:
      'pointer'
  },

  downloadButton: {
    display:
      'inline-block',
    border:
      '1px solid #2f6f9f',
    background:
      '#2f6f9f',
    color:
      'white',
    borderRadius:
      '5px',
    padding:
      '6px 10px',
    fontSize:
      '12px',
    fontWeight:
      '700',
    textDecoration:
      'none'
  },

  noFile: {
    color:
      '#829ab1',
    fontSize:
      '12px'
  },

  muted: {
    color:
      '#627d98',
    fontSize:
      '14px'
  },

  empty: {
    padding:
      '18px',
    background:
      'white',
    border:
      '1px solid #d9e2ec',
    borderRadius:
      '6px',
    color:
      '#627d98',
    fontSize:
      '14px'
  },

  error: {
    marginBottom:
      '14px',
    padding:
      '10px 12px',
    border:
      '1px solid #e6b8b8',
    borderRadius:
      '6px',
    background:
      '#fff5f5',
    color:
      '#8a1f1f',
    fontSize:
      '13px'
  },

  overlay: {
    position:
      'fixed',
    inset: 0,
    zIndex: 10000,
    display:
      'flex',
    alignItems:
      'center',
    justifyContent:
      'center',
    padding:
      '12px',
    background:
      'rgba(15, 35, 55, 0.58)'
  },

  preview: {
    width:
      'calc(100vw - 24px)',
    maxWidth:
      '1100px',
    height:
      'calc(100vh - 40px)',
    maxHeight:
      '900px',
    display:
      'flex',
    flexDirection:
      'column',
    background:
      'white',
    borderRadius:
      '8px',
    overflow:
      'hidden',
    boxShadow:
      '0 20px 60px rgba(0,0,0,0.25)'
  },

  previewHeader: {
    display:
      'flex',
    alignItems:
      'center',
    justifyContent:
      'space-between',
    gap:
      '12px',
    padding:
      '12px 14px',
    borderBottom:
      '1px solid #d9e2ec'
  },

  previewHeading: {
    display:
      'flex',
    flexDirection:
      'column',
    gap:
      '2px',
    minWidth: 0,
    color:
      '#243b53',
    fontSize:
      '14px'
  },

  closeButton: {
    border:
      'none',
    background:
      'transparent',
    color:
      '#627d98',
    fontSize:
      '25px',
    lineHeight: 1,
    cursor:
      'pointer'
  },

  previewFrame: {
    display:
      'block',
    width:
      '100%',
    flex: 1,
    minHeight: 0,
    border:
      'none',
    background:
      '#eef2f5'
  },

  previewFooter: {
    display:
      'flex',
    justifyContent:
      'flex-end',
    padding:
      '10px 14px',
    borderTop:
      '1px solid #d9e2ec'
  }
};
